import { Controller } from '@hotwired/stimulus';
import Quill from 'quill';
import type { EmitterSource } from 'quill';
import { jquery } from 'utils/jquery';
import { changedRange, indexAfterConversions, trimAfterLastVisibleCharacter } from './editor_text';
import {
  Delta, EMBED_PLACEHOLDER, VARIABLE_CLASS, isPillRightGuard, serializeVariables, variableDelta, variableMatchers,
  variableTokensIn, variableValue
} from './variable_blot';

const toolbarOptions = [
  [{ 'size': [] }],
  ['bold', 'italic', 'underline', 'link', 'video', 'strike'],
  [
    { 'color': ['#000000', '#FFFFFF', '#E60000', '#FF9900', '#FFFF00', '#008A00', '#0066CC', '#9933FF'] },
    { 'background': ['#FFFFFF', '#FACCCC', '#FFEBCC', '#FFFFCC', '#CCE8CC', '#CCE0F5', '#EBD6FF', '#BBBBBB'] }
  ],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'align': [] }],
  ['divider', 'clean']
];

const emailNoticeTollbarOptions = [
  [{ 'size': [] }],
  ['bold', 'italic', 'underline', 'link', 'strike'],
  [
    { 'color': ['#000000', '#FFFFFF', '#E60000', '#FF9900', '#FFFF00', '#008A00', '#0066CC', '#9933FF'] },
    { 'background': ['#FFFFFF', '#FACCCC', '#FFEBCC', '#FFFFCC', '#CCE8CC', '#CCE0F5', '#EBD6FF', '#BBBBBB'] }
  ],
  [{ 'list': 'bullet' }], [{ 'align': [] }], ['divider', 'clean']
];

const minimalToolbarOptions = [];

const TOOLBAR_STYLES = {
  default: toolbarOptions,
  minimal: minimalToolbarOptions,
  email: emailNoticeTollbarOptions
};

export default class extends Controller {
  static targets = ['input', 'container', 'textContainer'];

  declare inputTarget: HTMLInputElement;
  declare containerTarget: HTMLElement;
  declare textContainerTarget: HTMLElement;

  private saved = true;
  private converting = false;
  private settlingCaret = false;
  private convertedRange: { index: number; length: number } | null = null;
  private quill: Quill;
  private knownVariableNames: Set<string> | null = null;

  public initialize(): void {
    this.initializeQuill();

    // Remove trailing empty BreakBlot paragraph Quill adds on render, then sync hidden input.
    setTimeout(() => {
      this.removeTrailingBreaks();
      this.inputTarget.value = this.getEditorContent();
    }, 0);

    setInterval(this.updateInputValueWithQuillContent.bind(this), 2000);

    window.onbeforeunload = () => {
      if (!this.saved) {
        return 'There are unsaved changes. Are you sure you want to leave?';
      }
    };

    const parentComponent = window.EB.RichTextFields && window.EB.RichTextFields[this.containerTarget.dataset.id];
    parentComponent?.childrenInitCallback?.(this);
  }

  public connect(): void {
    document.addEventListener('submit', this.syncInputBeforeSubmit, true);
  }

  public disconnect(): void {
    document.removeEventListener('submit', this.syncInputBeforeSubmit, true);
  }

  // The input otherwise trails the editor by up to the 2 s timer, so a submit right after an edit
  // would send the old body. Capturing on document runs before every listener that reads the form.
  // No change is triggered: an autosave form submits on change, which would send a second request.
  private syncInputBeforeSubmit = (event: Event): void => {
    if (this.saved || event.target !== this.inputTarget.form) return;

    this.inputTarget.value = this.getEditorContent();
    this.saved = true;
  };

  private initializeQuill(): void {
    this.quill = new Quill(this.textContainerTarget, this.getOptions());

    // Intercept Shift+Enter before Quill handles it
    const editorElement = this.textContainerTarget.querySelector('.ql-editor') as HTMLElement;
    if (editorElement) {
      editorElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && e.shiftKey) {
          // Prevent Quill from receiving the event and creating a new <p> paragraph
          e.stopImmediatePropagation();
          e.preventDefault();

          const range = this.lockedVariables ? this.quill.getSelection() : null;
          if (range) {
            this.insertLineBreak(range.index, range.length);
            return;
          }

          // Insert a native line break at the cursor position
          document.execCommand('insertLineBreak');

          // Force Quill to sync the manually modified DOM with its internal model
          this.quill.update();
        }
      }, true); // capture mode: runs before Quill's own listener
    }

    if (this.lockedVariables) {
      this.registerVariableCopyHandlers(editorElement);
      this.registerPlainTextVariablePaste();
      this.registerPillCaretPlacement(editorElement);
      this.registerPillSelectionEnd();
      this.registerCompositionConversion();
    }

    this.quill.on('text-change', (delta: any, _oldDelta: unknown, source: string) => {
      if (this.lockedVariables && source === Quill.sources.USER) this.convertVariablesAfter(delta);
      this.saved = false;
      this.emitContentChangeEvent();
    });

    if (this.containerTarget.dataset.disabled === 'true') {
      this.quill.enable(false);
    }

    this.addToolbarTooltips();
    this.registerVideoHandler();
  }

  // Chrome's native line break goes inside a pill that ends the line, where Quill never sees it and the text
  // typed after it is lost, and before a pill it leaves the caret on the line above. The model has neither.
  private insertLineBreak(index: number, length: number): void {
    const [line, offset] = this.quill.getLine(index + length);
    const change = new Delta().retain(index).delete(length).insert({ break: true });
    // A <br> with nothing after it on its line renders no new line, so it is padded the way browsers do.
    if (line && offset === line.length() - 1) change.insert({ break: true });

    this.quill.updateContents(change, Quill.sources.USER);
    this.quill.setSelection(index + 1, 0, Quill.sources.SILENT);
  }

  // A pill is one index, so a click can only leave the caret before or after it. This runs on
  // mousedown because placing the native caret, or starting a word selection, is mousedown's
  // default action.
  private registerPillCaretPlacement(editorElement: HTMLElement | null): void {
    if (!editorElement) return;

    editorElement.addEventListener('mousedown', (event: MouseEvent) => {
      if (event.button !== 0 || event.shiftKey || !this.quill.isEnabled()) return;
      const pill = (event.target as Element).closest(`.${VARIABLE_CLASS}`);
      if (!pill) return;

      event.preventDefault();
      const index = this.pillIndex(pill);
      const box = pill.getBoundingClientRect();
      const after = event.clientX > box.left + box.width / 2;
      this.quill.setSelection(after ? index + 1 : index, 0, Quill.sources.USER);
    });

    // Quill reports only a selection that changed, so a click right of a pill the caret already sat
    // before is never reported. This runs before Quill's own mouseup update, on document.body.
    editorElement.addEventListener('mouseup', () => this.moveCaretOutOfRightGuard(Quill.sources.USER));

    // Listeners after this one still get the misread range, so the correction must not emit again.
    this.quill.on('selection-change', (range: any, _oldRange: unknown, source: string) => {
      if (source !== Quill.sources.USER || !range || range.length !== 0) return;
      this.moveCaretOutOfRightGuard(Quill.sources.SILENT);
    });
  }

  private pillIndex(pill: Element): number {
    return this.quill.getIndex(Quill.find(pill) as any);
  }

  // Quill reads a native caret at offset 0 of any node as the start of that node's blot, so the
  // caret Chrome leaves at offset 0 of a pill's right guard, where a click just right of the pill
  // lands, counts as before the pill.
  private moveCaretOutOfRightGuard(source: EmitterSource): void {
    if (this.settlingCaret) return;

    const native = window.getSelection();
    const guard = native?.anchorNode;
    if (!native?.isCollapsed || native.anchorOffset !== 0 || !isPillRightGuard(guard)) return;

    this.settlingCaret = true;
    try {
      const index = this.pillIndex(guard.parentElement as HTMLElement);
      this.quill.setSelection(index + 1, 0, source);
    } finally {
      this.settlingCaret = false;
    }
  }

  // The same shortcut reads a selection that ends at offset 0 of a pill's right guard, where a
  // drag that ends after a pill stops, as ending before the pill, so a highlighted pill alone on
  // its line read as a caret and a format applied to it changed nothing. A start there is left as
  // Quill reads it: Chrome starts a triple-click line selection right after a pill, and only that
  // reading keeps the pill in the selection.
  private registerPillSelectionEnd(): void {
    const { selection } = this.quill as any;
    const normalizedToRange = selection.normalizedToRange.bind(selection);

    selection.normalizedToRange = (range: any) => {
      const { end } = range;
      if (end.offset !== 0 || !isPillRightGuard(end.node)) return normalizedToRange(range);
      return normalizedToRange({ ...range, end: { node: end.node, offset: 1 } });
    };
  }

  public insertVariableOrText(index: number, text: string): void {
    if (this.lockedVariables) {
      const delta = variableDelta(text, this.variableNames) ?? new Delta().insert(text);
      this.quill.updateContents(new Delta().retain(index).concat(delta), Quill.sources.USER);
      this.quill.setSelection(index + delta.length(), 0, Quill.sources.SILENT);
    } else {
      this.quill.insertText(index, text, Quill.sources.USER);
      this.quill.setSelection(index + text.length, 0, Quill.sources.SILENT);
    }
  }

  // Quill's history replays undo and redo as source user. A conversion made inside a replay would go
  // unrecorded and leave both stacks stale, so only keystrokes, pastes and deletes convert.
  private convertVariablesAfter(change: any): void {
    if (this.quill.composition.isComposing) return;

    const range = changedRange(change.ops);
    if (range) this.convertVariablesBetween(range.start, range.end);
  }

  // The composition's text change is held back until it ends, so the one scan runs here, on the caret's line.
  private registerCompositionConversion(): void {
    this.quill.on(Quill.events.COMPOSITION_END, () => {
      const selection = this.quill.getSelection();
      if (!selection) return;

      const [line, offset] = this.quill.getLine(selection.index);
      if (line) this.convertVariablesBetween(selection.index - offset, selection.index - offset + line.length());
    });
  }

  // Only a token the change touches converts: after an undo restores a plain token, typing beside it must not
  // convert it again.
  private convertVariablesBetween(start: number, end: number): void {
    if (this.quill.history.ignoreChange || this.converting) return;

    const last = this.quill.getLength() - 1;
    const [firstLine, firstOffset] = this.quill.getLine(Math.min(start, last));
    const [lastLine, lastOffset] = this.quill.getLine(Math.min(end, last));
    if (!firstLine || !lastLine) return;

    const spanStart = Math.min(start, last) - firstOffset;
    const spanEnd = Math.min(end, last) - lastOffset + lastLine.length();

    const tokens: { start: number; length: number; name: string }[] = [];
    for (const line of this.quill.getLines(spanStart, spanEnd - spanStart)) {
      const lineStart = this.quill.getIndex(line);
      const text = (this.quill.getContents(lineStart, line.length()).ops as any[])
        .map((op) => (typeof op.insert === 'string' ? op.insert : EMBED_PLACEHOLDER))
        .join('');
      variableTokensIn(text).forEach((token) => {
        const tokenStart = lineStart + token.index;
        if (tokenStart < end && tokenStart + token.length > start) {
          tokens.push({ start: tokenStart, length: token.length, name: token.name });
        }
      });
    }
    if (tokens.length === 0) return;

    const selection = this.quill.getSelection();
    const history: any = this.quill.getModule('history');

    // Cut off on both sides so one undo reverts exactly the conversion, not the typing around it.
    this.converting = true;
    try {
      history.cutoff();
      for (let i = tokens.length - 1; i >= 0; i -= 1) {
        const { start: tokenStart, length, name } = tokens[i];
        const conversion = new Delta()
          .retain(tokenStart)
          .delete(length)
          .insert({ variable: variableValue(name, this.variableNames) }, this.inlineFormats(tokenStart, length));
        this.quill.updateContents(conversion, Quill.sources.USER);
      }
      history.cutoff();

      if (selection) {
        const from = indexAfterConversions(selection.index, tokens);
        const to = indexAfterConversions(selection.index + selection.length, tokens);
        this.convertedRange = { index: from, length: to - from };
        this.quill.setSelection(from, to - from, Quill.sources.SILENT);
      }
    } finally {
      this.converting = false;
    }
  }

  // getFormat also reports the line's formats and arrays for formats that differ across the span; only
  // inline formats with one value can be carried onto the pill.
  private inlineFormats(index: number, length: number): Record<string, unknown> {
    const { Scope } = Quill.import('parchment') as any;
    const formats: Record<string, unknown> = this.quill.getFormat(index, length);

    return Object.fromEntries(
      Object.entries(formats).filter(([name, value]) => !Array.isArray(value) && !this.quill.scroll.query(name, Scope.BLOCK))
    );
  }

  private emitContentChangeEvent(): void {
    const isEmpty = this.isEditorEmpty();

    const event = new CustomEvent('quill:content-changed', {
      detail: {
        isEmpty: isEmpty,
        content: this.quill.getText()
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  private get lockedVariables(): boolean {
    return this.containerTarget.dataset.lockedVariables === 'true';
  }

  private get variableNames(): Set<string> {
    if (!this.knownVariableNames) this.knownVariableNames = this.parseVariableNames();
    return this.knownVariableNames;
  }

  private parseVariableNames(): Set<string> {
    try {
      return new Set(JSON.parse(this.containerTarget.dataset.variables || '[]'));
    } catch {
      return new Set();
    }
  }

  private isEditorEmpty(): boolean {
    const text = this.quill.getText().trim();
    const hasImages = this.textContainerTarget.querySelector('.ql-editor img') !== null;
    const hasVideos = this.textContainerTarget.querySelector('.ql-editor iframe') !== null;
    // quill.getText() skips embeds, so a pill-only editor reads as empty without this.
    const hasVariables = this.textContainerTarget.querySelector(`.ql-editor .${VARIABLE_CLASS}`) !== null;

    return text === '' && !hasImages && !hasVideos && !hasVariables;
  }

  private updateInputValueWithQuillContent(): void {
    if (this.saved) return;

    const content = this.getEditorContent();
    this.inputTarget.value = content;

    // TODO: using jquery here as a workaround
    // need go investigate why 'dispatchEvent(new Event('change'))' is not working for all forms
    const $ = jquery();
    if ($) {
      $('.rich_text_textarea').trigger('change');
    }
    this.saved = true;
  }

  private removeTrailingBreaks(): void {
    const length = this.quill.getLength();
    if (length <= 1) return;

    const [lastLine] = this.quill.getLine(length - 1);
    if (!lastLine) return;

    const lineStart = this.quill.getIndex(lastLine);
    const lineContentLength = (lastLine as any).length() - 1; // exclude block's \n
    if (lineContentLength <= 0) return;

    // Only remove if the paragraph has no actual text (only BreakBlots / sentinel spans)
    const lastP = (lastLine as any).domNode as HTMLElement;
    const walker = document.createTreeWalker(lastP, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode()) !== null) {
      if ((node.textContent ?? '').trim() !== '') return;
    }
    if (lastP.querySelector(`img, iframe, .${VARIABLE_CLASS}`)) return;

    this.quill.deleteText(lineStart, lineContentLength, Quill.sources.SILENT);
  }

  private getEditorContent(): string {
    const root = this.textContainerTarget.querySelector('.ql-editor');
    if (!root) return '';

    // The flag-off trim below drops the closing tags when a <br> sits between the last character and
    // them. It stays byte-identical to master as the kill switch.
    if (this.lockedVariables) return trimAfterLastVisibleCharacter(serializeVariables(root));

    const html = root.innerHTML;

    // Scan backwards to find the last actual text character (not inside a tag, not whitespace).
    let lastTextPos = -1;
    let insideTag = false;
    for (let i = html.length - 1; i >= 0; i--) {
      const char = html[i];
      if (char === '>') { insideTag = true; continue; }
      if (char === '<') { insideTag = false; continue; }
      if (!insideTag && char.trim() !== '') { lastTextPos = i; break; }
    }

    if (lastTextPos === -1) return '';

    // Keep everything up to the last text character, then re-append only the
    // closing tags that immediately follow it so the HTML stays well-formed.
    const trailing = html.substring(lastTextPos + 1);
    const closingTags = trailing.match(/^(<\/[a-zA-Z][^>]*>)+/)?.[0] ?? '';
    return html.substring(0, lastTextPos + 1) + closingTags;
  }

  private getOptions(): any {
    const matchers: any[] = [
      ['BR', (_node: Node, _delta: unknown) => new Delta().insert({ break: true })]
    ];
    // Quill converts the initial innerHTML through these matchers, so this covers load and paste.
    if (this.lockedVariables) matchers.push(...variableMatchers(this.variableNames));

    return {
      modules: {
        toolbar: TOOLBAR_STYLES[this.containerTarget.dataset.toolbarStyle] || TOOLBAR_STYLES.default,
        clipboard: {
          matchers
        }
      },
      theme: 'snow',
      placeholder: this.containerTarget.dataset.placeholder || ''
    };
  }

  // Quill's own copy/cut handler writes text/plain from getText(), which drops embeds.
  // The selection is read in the capture phase because Quill's cut handler deletes it
  // before a later listener could; the write happens in the bubble phase to win over Quill's.
  private registerVariableCopyHandlers(editorElement: HTMLElement | null): void {
    if (!editorElement) return;

    let selectionText: string | null = null;

    const capture = () => {
      selectionText = this.selectionTextWithVariables();
    };
    const apply = (event: ClipboardEvent) => {
      if (selectionText !== null) event.clipboardData?.setData('text/plain', selectionText);
      selectionText = null;
    };

    ['copy', 'cut'].forEach((type) => {
      editorElement.addEventListener(type, capture, true);
      editorElement.addEventListener(type, apply);
    });
  }

  // Quill 2.0.0 Clipboard#convert returns a text-only paste (no text/html on the clipboard)
  // as one string insert without running the clipboard matchers, so variableTextMatcher
  // never sees it. Code blocks stay literal, as Quill keeps them.
  private registerPlainTextVariablePaste(): void {
    const clipboard: any = this.quill.getModule('clipboard');
    const convert = clipboard.convert.bind(clipboard);
    const codeBlock: string = (Quill.import('formats/code-block') as any).blotName;
    const known = this.variableNames;

    // Quill sets the caret from the pre-conversion text once it has applied a paste, which lands past a
    // pill the paste just completed. The conversion's caret is put back after it.
    const onPaste = clipboard.onPaste.bind(clipboard);
    clipboard.onPaste = (range: unknown, data: unknown) => {
      this.convertedRange = null;
      onPaste(range, data);
      const settled = this.convertedRange;
      if (settled) this.quill.setSelection(settled.index, settled.length, Quill.sources.SILENT);
    };

    clipboard.convert = (data: { html?: string; text?: string }, formats: Record<string, unknown> = {}) => {
      const delta = convert(data, formats);
      if (data.html || formats[codeBlock]) return delta;

      return variableDelta(data.text || '', known, formats) ?? delta;
    };
  }

  private selectionTextWithVariables(): string | null {
    const range = this.quill.getSelection();
    if (!range || range.length === 0) return null;

    const ops: any[] = this.quill.getContents(range.index, range.length).ops;
    if (!ops.some((op) => op.insert && typeof op.insert === 'object' && 'variable' in op.insert)) return null;

    return ops
      .map((op) => {
        if (typeof op.insert === 'string') return op.insert;
        return 'variable' in op.insert ? `{{${op.insert.variable.name}}}` : '';
      })
      .join('');
  }

  private registerVideoHandler(): void {
    const toolbar = this.quill.getModule('toolbar');
    toolbar.addHandler('video', this.videoHandler.bind(this));
  }

  private videoHandler(): void {
    const range = this.quill.getSelection(true);
    const url = prompt('Enter the video URL:');

    if (url) {
      const adjustedUrl = this.adjustVideoUrl(url);
      this.quill.insertEmbed(range.index, 'video', adjustedUrl, Quill.sources.USER);
      this.saved = false;
    }
  }

  private adjustVideoUrl(url: string): string {
    // YouTube (full and minified URLs)
    const ytRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?(.*&)?v=|youtube\.com\/embed\/|youtu\.be\/)([\w\-]{11})(\?.*)?$/;
    const ytMatch = url.match(ytRegex);
    if (ytMatch && ytMatch[2]) {
      const videoId = ytMatch[2];
      const queryParams = ytMatch[3] || '';
      return `https://www.youtube.com/embed/${videoId}${queryParams}`;
    }

    // Vimeo
    const vimeoRegex = /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:.*\/)?(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch && vimeoMatch[1]) {
      const videoId = vimeoMatch[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Wistia
    const wistiaRegex = /^(https?:\/\/(?:[\w\.-]+\.wistia\.com))\/(medias|embed)\/([a-zA-Z0-9]+)/;
    const wistiaMatch = url.match(wistiaRegex);
    if (wistiaMatch && wistiaMatch[1] && wistiaMatch[3]) {
      const domain = wistiaMatch[1];
      const videoId = wistiaMatch[3];
      return `${domain}/embed/iframe/${videoId}`;
    }

    // Dailymotion
    const dmRegex = /^(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/;
    const dmMatch = url.match(dmRegex);
    if (dmMatch && dmMatch[1]) {
      const videoId = dmMatch[1];
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    }

    return url;
  }

  private addToolbarTooltips(): void {
    const tooltips = [
        { selector: '.ql-picker-label', title: 'Font Size' },
        { selector: 'button.ql-bold', title: 'Bold' },
        { selector: 'button.ql-italic', title: 'Italic' },
        { selector: 'button.ql-underline', title: 'Underline' },
        { selector: 'button.ql-link', title: 'Link' },
        { selector: 'button.ql-strike', title: 'Strike' },
        { selector: '.ql-color.ql-color-picker', title: 'Color' },
        { selector: '.ql-background.ql-color-picker', title: 'Background Color' },
        { selector: 'button.ql-list', title: 'Ordered List' },
        { selector: 'button.ql-list[value="bullet"]', title: 'Bullet List' },
        { selector: '.ql-align', title: 'Align' },
        { selector: 'button.ql-video', title: 'Video' }
    ];

    tooltips.forEach(({ selector, title }) => {
        const element = this.containerTarget.querySelector(selector);
        if (element) {
            element.setAttribute('title', title);
        }
    });
  }
}
